import React, { useEffect } from "react";
import { Card, Col, Row } from "react-bootstrap";
import {
  StoreSection,
  useGetStoreCategoryPageQuery,
} from "../../../redux/services/store/storeCategoryPagesApi";

import CategoryHeroBannerSection from "./sections/CategoryHeroBannerSection";
import CategoryProductGridSection from "./sections/CategoryProductGridSection";
import PromoBannerSection from "./sections/PromoBannerSection";
import TextBlockSection from "./sections/TextBlockSection";

type Props = {
  categorySlug: string;
};

const renderSection = (section: StoreSection, categorySlug: string) => {
  switch (section.section_type) {
    case "category_hero_banner":
      return <CategoryHeroBannerSection section={section} />;

    case "category_product_grid":
      return (
        <CategoryProductGridSection
          section={section}
          categorySlug={categorySlug}
        />
      );

    case "promo_banner":
      return <PromoBannerSection section={section} />;

    case "text_block":
      return <TextBlockSection section={section} />;

    default:
      return null;
  }
};

const skeletonBaseStyle: React.CSSProperties = {
  width: "100%",
  background:
    "linear-gradient(90deg, rgba(240,240,240,0.95) 25%, rgba(248,248,248,1) 37%, rgba(240,240,240,0.95) 63%)",
  backgroundSize: "400% 100%",
  animation: "categoryPageRendererSkeletonShimmer 1.4s ease infinite",
};

const CategoryHeroSkeleton: React.FC = () => {
  return (
    <section>
      <div className="container-fluid container-custom">
        <Card className="border-0 overflow-hidden bg-light shadow-none">
          <Card.Body className="p-0">
            <div
              style={{
                ...skeletonBaseStyle,
                minHeight: 320,
                height: "100%",
              }}
            />
          </Card.Body>
        </Card>
      </div>
    </section>
  );
};

const CategoryGridSkeleton: React.FC = () => {
  return (
    <section>
      <div className="container-fluid container-custom">
        <Row className="justify-content-center mb-4">
          <Col lg={8}>
            <div className="text-center">
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 18,
                  width: 120,
                  borderRadius: 4,
                  margin: "0 auto 14px",
                }}
              />
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 34,
                  width: "56%",
                  borderRadius: 4,
                  margin: "0 auto 10px",
                }}
              />
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 16,
                  width: "42%",
                  borderRadius: 4,
                  margin: "0 auto",
                }}
              />
            </div>
          </Col>
        </Row>

        <Row className="g-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
            <Col key={`category-grid-skeleton-${item}`} xl={3} lg={4} md={6} xs={6}>
              <Card
                className="border-0 shadow-none overflow-hidden h-100"
                style={{
                  borderRadius: "22px",
                  background: "#fff",
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
                }}
              >
                <div
                  style={{
                    ...skeletonBaseStyle,
                    height: 0,
                    paddingBottom: "133.33%",
                  }}
                />
                <Card.Body className="p-3 p-lg-4">
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 14,
                      width: "30%",
                      borderRadius: 4,
                      marginBottom: 12,
                    }}
                  />
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 18,
                      width: "92%",
                      borderRadius: 4,
                      marginBottom: 8,
                    }}
                  />
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 18,
                      width: "70%",
                      borderRadius: 4,
                      marginBottom: 14,
                    }}
                  />
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 18,
                      width: "40%",
                      borderRadius: 4,
                      marginBottom: 16,
                    }}
                  />
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 42,
                      width: "100%",
                      borderRadius: 999,
                    }}
                  />
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

const PromoBannerSkeleton: React.FC = () => {
  return (
    <section>
      <div className="container-fluid container-custom">
        <div
          style={{
            ...skeletonBaseStyle,
            height: 180,
            borderRadius: 16,
          }}
        />
      </div>
    </section>
  );
};

const TextBlockSkeleton: React.FC = () => {
  return (
    <section>
      <div className="container-fluid container-custom">
        <Row className="justify-content-center">
          <Col lg={8}>
            <div className="text-center">
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 18,
                  width: 140,
                  borderRadius: 4,
                  margin: "0 auto 16px",
                }}
              />
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 16,
                  width: "100%",
                  borderRadius: 4,
                  marginBottom: 10,
                }}
              />
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 16,
                  width: "92%",
                  borderRadius: 4,
                  margin: "0 auto 10px",
                }}
              />
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 16,
                  width: "78%",
                  borderRadius: 4,
                  margin: "0 auto",
                }}
              />
            </div>
          </Col>
        </Row>
      </div>
    </section>
  );
};

const CategoryPageSkeleton: React.FC = () => {
  return (
    <>
      <style>
        {`
          @keyframes categoryPageRendererSkeletonShimmer {
            0% { background-position: 100% 0; }
            100% { background-position: 0 0; }
          }
        `}
      </style>

      <div
        style={{
          width: "100%",
          minHeight: "70vh",
          background: "#fff",
        }}
      >
        <CategoryHeroSkeleton />
        <CategoryGridSkeleton />
        <PromoBannerSkeleton />
        <TextBlockSkeleton />
      </div>
    </>
  );
};

const CategoryPageRenderer: React.FC<Props> = ({ categorySlug }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [categorySlug]);

  const { data, isLoading, isError } = useGetStoreCategoryPageQuery({
    categorySlug,
    includeItems: true,
    activeOnly: true,
  });

  if (isLoading) {
    return <CategoryPageSkeleton />;
  }

  if (isError || !data) {
    return <div style={{ minHeight: "70vh", background: "#fff" }} />;
  }

  const sections: StoreSection[] = (data.sections || [])
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  if (!sections.length) {
    return <div style={{ minHeight: "70vh", background: "#fff" }} />;
  }

  return (
    <div style={{ minHeight: "70vh", background: "#fff" }}>
      {sections.map((section) => (
        <React.Fragment key={section.id}>
          {renderSection(section, categorySlug)}
        </React.Fragment>
      ))}
    </div>
  );
};

export default CategoryPageRenderer;